import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Github, Twitter, Linkedin } from "lucide-react";
import { useLang, categoryName } from "@/i18n";
import { subscribeNewsletter } from "@/lib/api";

const CATS = ["gundem", "ekonomi", "spor", "teknoloji", "dunya", "saglik"];

export default function Footer() {
  const { lang, t } = useLang();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await subscribeNewsletter(email);
      toast.success(t("newsletter_success"));
      setEmail("");
    } catch {
      toast.error("Error. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-black text-neutral-200 mt-24 border-t border-neutral-800" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Link to="/" className="flex items-baseline gap-1" data-testid="footer-logo">
              <span className="headline text-3xl text-white">loft</span>
              <span className="font-heading text-3xl text-red-500">.</span>
              <span className="headline text-3xl text-white">press</span>
            </Link>
            <p className="mt-4 text-sm text-neutral-400 max-w-md leading-relaxed">{t("footer_tagline")}</p>

            <div className="mt-8">
              <h3 className="headline text-xl text-white mb-2">{t("newsletter_title")}</h3>
              <p className="text-sm text-neutral-400 mb-4">{t("newsletter_desc")}</p>
              <form onSubmit={onSubscribe} className="flex gap-2 max-w-sm" data-testid="newsletter-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("newsletter_placeholder")}
                  className="flex-1 bg-neutral-900 border border-neutral-700 text-white px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-red-500"
                  data-testid="newsletter-email-input"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm disabled:opacity-60"
                  data-testid="newsletter-submit-btn"
                >
                  {t("newsletter_submit")}
                </button>
              </form>
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="eyebrow text-neutral-500 mb-4">{t("footer_categories")}</h4>
            <ul className="space-y-2 text-sm">
              {CATS.map((c) => (
                <li key={c}>
                  <Link to={`/category/${c}`} className="text-neutral-300 hover:text-red-500 transition-colors" data-testid={`footer-cat-${c}`}>
                    {categoryName(c, lang)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="eyebrow text-neutral-500 mb-4">{t("footer_developers")}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/marketplace" className="text-neutral-300 hover:text-red-500 transition-colors" data-testid="footer-marketplace">{t("nav_marketplace")}</Link></li>
              <li><Link to="/marketplace/docs" className="text-neutral-300 hover:text-red-500 transition-colors" data-testid="footer-docs">{t("nav_docs")}</Link></li>
              <li><Link to="/faq" className="text-neutral-300 hover:text-red-500 transition-colors" data-testid="footer-faq">{t("footer_faq")}</Link></li>
              <li><Link to="/status" className="text-neutral-300 hover:text-red-500 transition-colors" data-testid="footer-status">{t("footer_status")}</Link></li>
              <li><Link to="/changelog" className="text-neutral-300 hover:text-red-500 transition-colors" data-testid="footer-changelog">{t("footer_changelog")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="eyebrow text-neutral-500 mb-4">{t("footer_company")}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-neutral-300 hover:text-red-500 transition-colors" data-testid="footer-about">{t("footer_about_us")}</Link></li>
              <li><Link to="/contact" className="text-neutral-300 hover:text-red-500 transition-colors" data-testid="footer-contact">{t("footer_contact")}</Link></li>
              <li><Link to="/careers" className="text-neutral-300 hover:text-red-500 transition-colors" data-testid="footer-careers">{t("footer_careers")}</Link></li>
            </ul>
            <h4 className="eyebrow text-neutral-500 mt-6 mb-4">{t("footer_legal")}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/terms" className="text-neutral-300 hover:text-red-500 transition-colors" data-testid="footer-terms">{t("footer_terms")}</Link></li>
              <li><Link to="/privacy" className="text-neutral-300 hover:text-red-500 transition-colors" data-testid="footer-privacy">{t("footer_privacy")}</Link></li>
              <li><Link to="/refund" className="text-neutral-300 hover:text-red-500 transition-colors" data-testid="footer-refund">Refund Policy</Link></li>
              <li><Link to="/pricing" className="text-neutral-300 hover:text-red-500 transition-colors" data-testid="footer-pricing">Pricing</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs font-mono text-neutral-500" data-testid="footer-copyright">
            © {new Date().getFullYear()} loft.press — All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-neutral-500 hover:text-white transition-colors" aria-label="Twitter"><Twitter size={18} /></a>
            <a href="#" className="text-neutral-500 hover:text-white transition-colors" aria-label="GitHub"><Github size={18} /></a>
            <a href="#" className="text-neutral-500 hover:text-white transition-colors" aria-label="LinkedIn"><Linkedin size={18} /></a>
          </div>
        </div>
      </div>
    </footer>
  );
}
